import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { CardConfiguration } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { UserRecord, CardType, CardSearchResponse } from '../../user.model';
import { UserService } from '../../service/user.abstract.service';
import { CreateCardDialogComponent } from '../create-card-dialog/create-card-dialog.component';
import { InformationalDialogComponent, ErrorSeverity } from 'src/app/global/components/dialogs/informational-dialog/informational-dialog.component';
import { organisationConfigurationSelector, languageSelector, organisationTagSelector } from 'src/app/global/store/global.selectors';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';

@Component({
    selector: 'app-link-card-dialog',
    templateUrl: './link-card-dialog.component.html',
    styleUrls: ['./link-card-dialog.component.scss'],
})
export class LinkCardDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    processingSubmit = false;
    userId: string;
    public form: any = new FormGroup({
        cardType: new FormControl('', [Validators.required]),
        cardNumber: new FormControl('', [Validators.required]),
    });

    cardConfig: CardConfiguration;
    cardLabel: string;
    cardIcon: string;
    searchResult: CardSearchResponse | null = null;
    searchLoading = false;
    useSearchFlow = false;

    constructor(
        private translateService: TranslateService,
        private userService: UserService,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<CreateCardDialogComponent>,
        private store: Store<State>,
        private messageService: MessageService,
        @Inject(MAT_DIALOG_DATA) private data: { cardType: CardType; userRecord: UserRecord },
    ) {}

    ngOnInit() {
        this.form.patchValue({ cardType: this.data.cardType });
        this.userId = this.data.userRecord.id;

        combineLatest([this.store.select(languageSelector), this.store.select(organisationConfigurationSelector), this.store.select(organisationTagSelector)])
            .pipe(
                map(([lang, orgConfig, orgTag]) => {
                    this.cardConfig = orgConfig?.support?.cards?.find((c) => c.type === this.data.cardType);
                    this.cardLabel = this.cardConfig.identity?.label[lang];
                    this.cardIcon = this.cardConfig?.identity?.icon || 'icon-my-card';
                    this.useSearchFlow = orgTag === 'STECATHERINE' && this.data.cardType === CardType.LIBRARY;
                }),
            )
            .subscribe();
    }

    public closeDialog(val: boolean = false) {
        this.dialogRef.close(val);
    }

    public validateCardLinkForm() {
        return this.form.valid;
    }

    public submit() {
        if (this.useSearchFlow && !this.searchResult) {
            this.searchBeforeLink();
            return;
        }

        this.processingSubmit = true;
        const payload = {
            cardType: this.form.get('cardType').value as string,
            cardNumber: this.form.get('cardNumber').value as string,
        };
        this.userService
            .postLinkCardAdmin(payload, this.userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.closeDialog(true);
                    this.processingSubmit = false;
                    this.messageService.success(
                        this.translateService.instant('pages.user-cards.link.messages.success', {
                            cardType: this.cardLabel,
                            cardNumber: this.form.get('cardNumber').value,
                        }),
                        undefined,
                        { enableHtml: true, timeOut: 5000 },
                    );
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(error?.error?.messages?.[0] || this.translateService.instant('pages.user-cards.link.messages.error'));
                },
            );
    }

    public searchBeforeLink(): void {
        this.searchLoading = true;
        const payload = {
            cardType: this.data.cardType,
            cardNumber: this.form.get('cardNumber').value,
            firstName: this.data.userRecord.firstName,
            lastName: this.data.userRecord.lastName,
            birthDate: this.data.userRecord.birthDate,
        };

        this.userService
            .searchBeforeCreate(this.userId, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.searchResult = result;
                    this.searchLoading = false;

                    if (result.status === 'LINKED') {
                        this.dialogRef.close(true);
                    }
                },
                error: () => {
                    this.searchLoading = false;
                },
            });
    }

    public confirmAlternativeCard(): void {
        const cardNumber = this.searchResult?.card?.code || this.searchResult?.card?.number;
        if (!cardNumber) return;
        this.form.patchValue({ cardNumber });
        this.searchResult = null;
        this.searchBeforeLink();
    }

    public resetSearch(): void {
        this.searchResult = null;
    }

    openErrorDialog(cardNumber: string) {
        this.dialog
            .open(InformationalDialogComponent, {
                panelClass: 'bciti-error-dialog',
                data: {
                    title: this.translateService.instant('pages.user-cards.link.messages.cardAlreadyLinked.title'),
                    body: this.translateService.instant('pages.user-cards.link.messages.cardAlreadyLinked.description', { cardNumber: cardNumber }),
                    hideIcon: false,
                    errorSeverity: ErrorSeverity.Error,
                },
            })
            .afterClosed()
            .subscribe(() => {
                this.dialogRef.removePanelClass('is-hidden');
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
